import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import InlineConfirm from '../../components/InlineConfirm';
import { IconChevronD, IconPlus, IconSearch, IconTrash, IconX } from '../../components/Icons';
import {
  AdminFeaturedGame,
  FeaturedGameCandidate,
  addFeaturedGame,
  getAdminFeaturedGames,
  removeFeaturedGame,
  reorderFeaturedGames,
  searchFeaturedGameCandidates,
  uploadFeaturedGameVideo,
} from '../../services/adminFeaturedGames';
import AdminPage from './AdminPage';

const errorMessage = (err: unknown, fallback: string) =>
  (axios.isAxiosError(err) && (err.response?.data?.error as string | undefined)) || fallback;

function StatusChip({ game }: { game: AdminFeaturedGame }) {
  if (!game.isPlayable) {
    return (
      <span className="chip warn">
        <span className="dot" />
        Not published · hidden
      </span>
    );
  }
  if (!game.videoUrl) {
    return (
      <span className="chip warn">
        <span className="dot" />
        Needs a video · hidden
      </span>
    );
  }
  return (
    <span className="chip ok">
      <span className="dot" />
      On the landing page
    </span>
  );
}

function Preview({ game }: { game: AdminFeaturedGame }) {
  return (
    <div className="frame" style={{ width: 66, flex: 'none' }}>
      <div className="frame-inner" style={{ aspectRatio: '3 / 4' }}>
        {game.videoUrl ? (
          <video src={game.videoUrl} poster={game.thumbnailUrl ?? undefined} muted loop playsInline autoPlay />
        ) : game.thumbnailUrl ? (
          <img src={game.thumbnailUrl} alt="" />
        ) : null}
      </div>
    </div>
  );
}

function AddGameDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<FeaturedGameCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      searchFeaturedGameCandidates(search.trim())
        .then(setCandidates)
        .catch((err) => setError(errorMessage(err, 'Could not load games.')))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const add = async (projectId: string) => {
    setAdding(projectId);
    setError(null);
    try {
      await addFeaturedGame(projectId);
      onAdded();
    } catch (err) {
      setError(errorMessage(err, 'Could not add the game.'));
      setAdding(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div className="modal-title">Add a featured game</div>
          <button type="button" className="btn ghost sm icon" onClick={onClose} aria-label="Close">
            <IconX size={16} />
          </button>
        </div>
        <p className="modal-body">Only published games with a ready build can be featured.</p>
        <div style={{ position: 'relative', margin: '16px 0 12px' }}>
          <span style={{ position: 'absolute', left: 14, top: 14, color: 'var(--text-5)' }}>
            <IconSearch size={16} />
          </span>
          <input
            className="input"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by game or creator"
            style={{ paddingLeft: 40 }}
          />
        </div>
        {error && <div className="field"><div className="err">{error}</div></div>}
        <div className="scroll-thin" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
          {loading ? (
            <div className="muted" style={{ padding: 12 }}>Loading…</div>
          ) : candidates.length === 0 ? (
            <div className="muted" style={{ padding: 12 }}>No published games match.</div>
          ) : (
            candidates.map((c) => (
              <button
                key={c.projectId}
                type="button"
                className="opt"
                disabled={adding !== null}
                onClick={() => add(c.projectId)}
                style={{ padding: '10px 14px' }}
              >
                <div className="frame" style={{ width: 40, flex: 'none' }}>
                  <div className="frame-inner" style={{ aspectRatio: '1 / 1' }}>
                    {c.thumbnailUrl && <img src={c.thumbnailUrl} alt="" />}
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="opt-title">{c.name}</div>
                  <div className="opt-sub">by {c.creatorName}</div>
                </div>
                <span className="opt-meta muted">{adding === c.projectId ? 'Adding…' : <IconPlus size={14} />}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function FeaturedGames() {
  const [games, setGames] = useState<AdminFeaturedGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(() => {
    getAdminFeaturedGames()
      .then((list) => {
        setGames(list);
        setError(null);
      })
      .catch((err) => setError(errorMessage(err, 'Could not load featured games.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= games.length) return;
    const next = [...games];
    [next[index], next[target]] = [next[target], next[index]];
    setGames(next);
    try {
      await reorderFeaturedGames(next.map((g) => g.projectId));
    } catch (err) {
      setError(errorMessage(err, 'Could not save the new order.'));
      load();
    }
  };

  const upload = async (game: AdminFeaturedGame, file: File) => {
    setBusy(game.projectId);
    setError(null);
    try {
      const updated = await uploadFeaturedGameVideo(game.projectId, file);
      setGames((list) => list.map((g) => (g.projectId === updated.projectId ? updated : g)));
    } catch (err) {
      setError(errorMessage(err, 'Could not upload the video.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (game: AdminFeaturedGame) => {
    setConfirmingRemoval(null);
    setBusy(game.projectId);
    setError(null);
    try {
      await removeFeaturedGame(game.projectId);
      setGames((list) => list.filter((g) => g.projectId !== game.projectId));
    } catch (err) {
      setError(errorMessage(err, 'Could not remove the game.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminPage
      active="admin-featured"
      eyebrow="Landing page"
      title="Featured games"
      intro={
        'These games show on the landing page in this order. A game shows only while it is published and has a ' +
        'preview video: an mp4, ideally 3:4 and 10 seconds or shorter.'
      }
      rightSlot={
        <button type="button" className="btn primary sm" onClick={() => setShowAdd(true)}>
          <IconPlus size={14} />
          Add game
        </button>
      }
    >
      {error && (
        <div
          style={{
            padding: '10px 14px',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-wash)',
            color: 'var(--danger)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : games.length === 0 ? (
        <div className="card quiet" style={{ padding: 32, textAlign: 'center' }}>
          <div className="t-h3" style={{ marginBottom: 8 }}>No featured games yet</div>
          <p className="t-body" style={{ margin: '0 0 18px' }}>The landing page hides the games section until you add one.</p>
          <button type="button" className="btn primary sm" onClick={() => setShowAdd(true)}>
            <IconPlus size={14} />
            Add game
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {games.map((game, i) => (
            <div key={game.projectId} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  type="button"
                  className="btn ghost sm icon"
                  style={{ width: 30, height: 30 }}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  aria-label="Move up"
                >
                  <IconChevronD size={14} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <button
                  type="button"
                  className="btn ghost sm icon"
                  style={{ width: 30, height: 30 }}
                  disabled={i === games.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label="Move down"
                >
                  <IconChevronD size={14} />
                </button>
              </div>
              <span className="t-num" style={{ width: 20 }}>{String(i + 1).padStart(2, '0')}</span>
              <Preview game={game} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  <a href={`/play/${game.projectId}`} target="_blank" rel="noreferrer" style={{ color: 'var(--text)' }}>
                    {game.name}
                  </a>
                </div>
                <div className="muted" style={{ fontSize: 12.5, margin: '4px 0 10px' }}>by {game.creatorName}</div>
                <StatusChip game={game} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  ref={(el) => (fileInputs.current[game.projectId] = el)}
                  type="file"
                  accept="video/mp4"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) upload(game, file);
                  }}
                />
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy === game.projectId}
                  onClick={() => fileInputs.current[game.projectId]?.click()}
                >
                  {busy === game.projectId ? 'Working…' : game.videoUrl ? 'Replace video' : 'Upload video'}
                </button>
                {confirmingRemoval === game.projectId ? (
                  <InlineConfirm
                    question={`Remove "${game.name}" from the landing page? The game stays published.`}
                    confirmLabel="Yes, remove"
                    danger
                    onConfirm={() => remove(game)}
                    onCancel={() => setConfirmingRemoval(null)}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn danger sm icon"
                    disabled={busy === game.projectId}
                    onClick={() => setConfirmingRemoval(game.projectId)}
                    aria-label={`Remove ${game.name}`}
                  >
                    <IconTrash size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddGameDialog
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </AdminPage>
  );
}
